import {
  Component,
  OnInit,
  Input,
  ViewChild,
  AfterContentInit,
  ComponentRef,
  ChangeDetectorRef,
  inject,
  ViewContainerRef,
  Type,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { CommonKeys } from "../../formular-model/common/enums";

import { FormularGroupTabContentComponent } from "./tab-content.interface";
import { FormularInitTracker } from "../../services/init-tracker/formular-init-tracker.service";
import { TAB_TYPE_MAP } from "./tab-typemap";

@Component({
  selector: "app-formular-group-tab",
  templateUrl: "./group-tab.component.html",
  styleUrls: ["./group-tab.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormularGroupTabComponent implements OnInit, AfterContentInit {
  private _cdr = inject(ChangeDetectorRef);
  private _tracker = inject(FormularInitTracker);

  @Input() data!: any;
  @Input() form!: FormGroup;
  @Input() tabKey!: string;

  public childComponent!: ComponentRef<FormularGroupTabContentComponent>;

  @ViewChild("tabContentHost", { static: true, read: ViewContainerRef })
  private _tabContentHost!: ViewContainerRef;

  ngOnInit(): void {
    if (!this.tabKey) {
      throw new Error(
        `FormularGroupTabComponent: tabKey input is required (group type: ${this.data?.[CommonKeys.Type]})`,
      );
    }

    this._tracker.register(this.tabKey);
  }

  ngAfterContentInit(): void {
    try {
      const groupType = this.data[CommonKeys.Type];
      const componentType = TAB_TYPE_MAP.get(groupType);
      if (!componentType) {
        throw new Error(`Unsupported group type: ${groupType}`);
      }
      this._loadComponent(componentType);
      this._cdr.detectChanges();
      this._tracker.markReady(this.tabKey);
    } catch (error) {
      this._tracker.markError(this.tabKey, error as Error);
    }
  }

  private _loadComponent<T extends FormularGroupTabContentComponent>(
    componentType: Type<T>,
  ): void {
    const componentRef = this._tabContentHost.createComponent(componentType);
    this.childComponent = componentRef;
    const child = componentRef.instance;

    this._wrapLifecycleHooks(child);

    componentRef.setInput("data", this.data);
    componentRef.setInput("form", this.form);

    child._reportError = (e: Error) => {
      this._tracker.markError(this.tabKey, e);
    };
  }

  private _wrapLifecycleHooks(child: any): void {
    const hooks = [
      "ngOnChanges",
      "ngOnInit",
      "ngDoCheck",
      "ngAfterContentInit",
      "ngAfterViewInit",
    ];

    for (const hook of hooks) {
      const original = child[hook];
      if (typeof original !== "function") continue;

      child[hook] = (...args: any[]) => {
        try {
          return original.apply(child, args);
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          this._tracker.markError(this.tabKey, err);
        }
      };
    }
  }

  public async getTabData(): Promise<any> {
    return await this.childComponent.instance.reconstructData();
  }
}
